import React, { useState } from "react";
import axios from "axios";
import { CheckCircle2, ArrowRight, MessageCircle, Loader2 } from "lucide-react";
import { useLanguage } from "../../contexts/LanguageContext";
import { CONTACT } from "../../lib/constants";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const initialValues = {
  name: "",
  email: "",
  phone: "",
  session_type: "",
  preferred_date: "",
  people: "",
  location: "",
  message: "",
  gdpr_consent: false,
};

const inputBase =
  "w-full bg-white border border-border-soft px-4 py-3 text-sm text-accent-dark font-sans focus:outline-none focus:border-gold transition-colors placeholder:text-text-muted/60";
const labelBase =
  "block text-[11px] tracking-[0.2em] uppercase text-text-muted font-medium mb-2";

export const BookingForm = () => {
  const { t } = useLanguage();
  const [values, setValues] = useState(initialValues);
  const [status, setStatus] = useState("idle");
  const [errorMsg, setErrorMsg] = useState("");

  const tf = t.bookingForm;

  const update = (field) => (e) => {
    const val =
      e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setValues((v) => ({ ...v, [field]: val }));
  };

  const buildWhatsappMessage = (v) => {
    const lines = [
      "New booking request from clickphotography.gr",
      `Name: ${v.name}`,
      `Email: ${v.email}`,
      `Phone: ${v.phone}`,
      `Session: ${v.session_type}`,
      v.preferred_date && `Date: ${v.preferred_date}`,
      v.people && `People: ${v.people}`,
      v.location && `Location: ${v.location}`,
      v.message && `Message: ${v.message}`,
    ].filter(Boolean);
    return encodeURIComponent(lines.join("\n"));
  };

  const openWhatsappWith = (v) => {
    const url = `https://wa.me/${CONTACT.whatsappRaw}?text=${buildWhatsappMessage(v)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (status === "sending") return;
    setErrorMsg("");
    setStatus("sending");
    try {
      const payload = {
        ...values,
        people: values.people ? Number(values.people) : null,
        preferred_date: values.preferred_date || null,
        location: values.location || null,
        message: values.message || null,
      };
      await axios.post(`${API}/bookings`, payload);
      setStatus("success");
      setTimeout(() => {
        try {
          openWhatsappWith(values);
        } catch (e) {
          // ignore popup blockers
        }
      }, 900);
    } catch (err) {
      setStatus("error");
      const detail =
        err?.response?.data?.detail && typeof err.response.data.detail === "string"
          ? err.response.data.detail
          : tf.errorGeneric;
      setErrorMsg(detail);
    }
  };

  const reset = () => {
    setValues(initialValues);
    setStatus("idle");
    setErrorMsg("");
  };

  if (status === "success") {
    return (
      <div
        data-testid="booking-success"
        className="bg-white border border-border-soft p-10 md:p-14 text-center"
      >
        <div className="w-14 h-14 mx-auto rounded-full bg-bg-alt flex items-center justify-center text-gold">
          <CheckCircle2 size={28} strokeWidth={1.4} />
        </div>
        <h3 className="font-serif text-2xl md:text-3xl text-accent-dark mt-6 font-light">
          {tf.successTitle}
        </h3>
        <p className="mt-4 text-text-muted leading-relaxed max-w-lg mx-auto">
          {tf.successBody}
        </p>
        <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
          <button
            data-testid="booking-success-whatsapp"
            onClick={() => openWhatsappWith(values)}
            className="btn-luxury"
            style={{ padding: "0.9rem 1.75rem", fontSize: "0.7rem" }}
          >
            <MessageCircle size={14} /> {tf.openWhatsapp}
          </button>
          <button
            data-testid="booking-send-another"
            onClick={reset}
            className="text-[11px] tracking-[0.2em] uppercase font-medium text-text-muted hover:text-accent-dark transition-colors"
          >
            {tf.sendAnother}
          </button>
        </div>
      </div>
    );
  }

  return (
    <form
      data-testid="booking-form"
      onSubmit={handleSubmit}
      className="bg-white border border-border-soft p-6 md:p-10 space-y-6"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div>
          <label className={labelBase} htmlFor="bf-name">
            {tf.fields.name} <span className="text-gold">*</span>
          </label>
          <input
            id="bf-name"
            data-testid="bf-name"
            type="text"
            required
            minLength={2}
            value={values.name}
            onChange={update("name")}
            placeholder={tf.placeholders.name}
            className={inputBase}
          />
        </div>
        <div>
          <label className={labelBase} htmlFor="bf-email">
            {tf.fields.email} <span className="text-gold">*</span>
          </label>
          <input
            id="bf-email"
            data-testid="bf-email"
            type="email"
            required
            value={values.email}
            onChange={update("email")}
            placeholder={tf.placeholders.email}
            className={inputBase}
          />
        </div>
        <div>
          <label className={labelBase} htmlFor="bf-phone">
            {tf.fields.phone} <span className="text-gold">*</span>
          </label>
          <input
            id="bf-phone"
            data-testid="bf-phone"
            type="tel"
            required
            value={values.phone}
            onChange={update("phone")}
            placeholder={tf.placeholders.phone}
            className={inputBase}
          />
        </div>
        <div>
          <label className={labelBase} htmlFor="bf-session">
            {tf.fields.sessionType} <span className="text-gold">*</span>
          </label>
          <select
            id="bf-session"
            data-testid="bf-session"
            required
            value={values.session_type}
            onChange={update("session_type")}
            className={`${inputBase} appearance-none pr-10`}
          >
            <option value="" disabled>
              —
            </option>
            {tf.sessionOptions.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className={labelBase} htmlFor="bf-date">
            {tf.fields.date}
          </label>
          <input
            id="bf-date"
            data-testid="bf-date"
            type="date"
            value={values.preferred_date}
            onChange={update("preferred_date")}
            className={inputBase}
          />
        </div>
        <div>
          <label className={labelBase} htmlFor="bf-people">
            {tf.fields.people}
          </label>
          <input
            id="bf-people"
            data-testid="bf-people"
            type="number"
            min={1}
            max={50}
            value={values.people}
            onChange={update("people")}
            placeholder="2"
            className={inputBase}
          />
        </div>
        <div className="md:col-span-2">
          <label className={labelBase} htmlFor="bf-location">
            {tf.fields.location}
          </label>
          <input
            id="bf-location"
            data-testid="bf-location"
            type="text"
            value={values.location}
            onChange={update("location")}
            placeholder={tf.placeholders.location}
            className={inputBase}
          />
        </div>
        <div className="md:col-span-2">
          <label className={labelBase} htmlFor="bf-message">
            {tf.fields.message}
          </label>
          <textarea
            id="bf-message"
            data-testid="bf-message"
            rows={4}
            value={values.message}
            onChange={update("message")}
            placeholder={tf.placeholders.message}
            className={`${inputBase} resize-none`}
          />
        </div>
      </div>

      <label className="flex items-start gap-3 text-sm text-text-muted leading-relaxed cursor-pointer select-none">
        <input
          type="checkbox"
          data-testid="bf-consent"
          required
          checked={values.gdpr_consent}
          onChange={update("gdpr_consent")}
          className="mt-1 w-4 h-4 accent-[#C8A97E] shrink-0"
        />
        <span>
          {tf.consent}{" "}
          <a
            href="/privacy"
            target="_blank"
            rel="noreferrer"
            className="text-gold hover:underline"
          >
            Privacy Policy
          </a>
          .
        </span>
      </label>

      {status === "error" && errorMsg && (
        <p
          data-testid="booking-error"
          className="text-sm text-red-600 bg-red-50 border border-red-200 px-4 py-3"
        >
          {errorMsg}
        </p>
      )}

      <div className="flex flex-col sm:flex-row items-center gap-4 pt-2">
        <button
          type="submit"
          data-testid="bf-submit"
          disabled={status === "sending"}
          className="btn-luxury disabled:opacity-60 disabled:cursor-not-allowed"
          style={{ padding: "1rem 2rem", fontSize: "0.7rem" }}
        >
          {status === "sending" ? (
            <>
              <Loader2 size={14} className="animate-spin" /> {tf.sending}
            </>
          ) : (
            <>
              {tf.submit} <ArrowRight size={14} />
            </>
          )}
        </button>
        <p className="text-xs text-text-muted">
          <span className="text-gold">*</span> {tf.required}
        </p>
      </div>
    </form>
  );
};
